/**
 * BT-058 / BT-059 — Avatar persistence (local demo + backend hybrid).
 */
import { getBackendApiUrl, isHybridDataMode } from "@/config/backendStaging049.js";
import {
  getBackendToken,
  getStoredBackendUser,
  persistBackendAuth,
  apiFetchMe,
} from "@/api/karateBackendApi.js";

export const KARATE_AVATAR_STORAGE_KEY = "karate-phase1-avatars-v1";
export const KARATE_AVATAR_EVENT = "karate-avatar-updated";

function readStore() {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(KARATE_AVATAR_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeStore(next) {
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(KARATE_AVATAR_STORAGE_KEY, JSON.stringify(next));
  }
  window.dispatchEvent(new Event(KARATE_AVATAR_EVENT));
}

function normalizeStoredAvatarPath(url) {
  if (!url || typeof url !== "string") return null;
  if (url.startsWith("blob:")) return null;
  if (url.startsWith("/api/v1/avatars/")) return url;
  const base = getBackendApiUrl();
  if (base && url.startsWith(base)) {
    return url.slice(base.length);
  }
  try {
    const parsed = new URL(url);
    if (parsed.pathname.startsWith("/api/v1/avatars/")) {
      return `${parsed.pathname}${parsed.search}`;
    }
  } catch {
    /* not a URL */
  }
  return url;
}

export function resolveBackendAvatarUrl(pathOrUrl) {
  if (!pathOrUrl) return null;
  if (pathOrUrl.startsWith("blob:")) return null;
  if (pathOrUrl.startsWith("data:")) return pathOrUrl;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const base = getBackendApiUrl();
  if (!base) return pathOrUrl;
  return `${base.replace(/\/$/, "")}${pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`}`;
}

export function getLocalAvatarPath(personKey) {
  if (!personKey) return null;
  const store = readStore();
  const entry = store[personKey];
  if (!entry?.path) return null;
  return normalizeStoredAvatarPath(entry.path);
}

export function getLocalAvatarUrl(personKey) {
  const path = getLocalAvatarPath(personKey);
  return path ? resolveBackendAvatarUrl(path) : null;
}

export function setLocalAvatarPath(personKey, avatarPath) {
  if (!personKey) return;
  const store = readStore();
  const normalized = normalizeStoredAvatarPath(avatarPath);
  if (!normalized) {
    delete store[personKey];
  } else {
    store[personKey] = { path: normalized, updatedAt: Date.now() };
  }
  writeStore(store);
}

/** @deprecated use setLocalAvatarPath */
export function setLocalAvatarUrl(personKey, url) {
  setLocalAvatarPath(personKey, url);
}

export function updateStoredBackendUserAvatar({ avatarUrl, person } = {}) {
  if (typeof localStorage === "undefined") return;
  try {
    const raw = localStorage.getItem("karate-backend-auth-v1");
    if (!raw) return;
    const stored = JSON.parse(raw);
    if (!stored?.user) return;
    const nextPerson = person
      ? { ...stored.user.person, ...person, avatarUrl: avatarUrl ?? person.avatarUrl ?? person.avatar_url ?? null }
      : stored.user.person
        ? { ...stored.user.person, avatarUrl: avatarUrl ?? stored.user.person.avatarUrl ?? null }
        : null;
    persistBackendAuth({
      ...stored,
      user: {
        ...stored.user,
        person: nextPerson,
      },
    });
    const personId = nextPerson?.id || stored.user.personId;
    if (personId && !isHybridDataMode()) {
      setLocalAvatarPath(personId, avatarUrl ?? nextPerson?.avatarUrl ?? null);
    }
  } catch {
    /* ignore */
  }
}

export function clearLocalAvatarCache() {
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(KARATE_AVATAR_STORAGE_KEY);
  }
}

export async function syncProfileAvatarFromBackend() {
  const token = getBackendToken();
  if (!token || !isHybridDataMode()) return null;
  try {
    const data = await apiFetchMe();
    const person = data?.user?.person;
    if (!person?.id) return null;
    const avatarPath = person.avatarUrl || person.avatar_url || null;
    updateStoredBackendUserAvatar({ avatarUrl: avatarPath, person });
    if (avatarPath) {
      if (!isHybridDataMode()) setLocalAvatarPath(person.id, avatarPath);
    } else if (!isHybridDataMode()) {
      setLocalAvatarPath(person.id, null);
    }
    return avatarPath ? resolveBackendAvatarUrl(avatarPath) : null;
  } catch {
    return null;
  }
}

function getStoredAuthAvatarPath(personId) {
  if (!personId || !isHybridDataMode()) return null;
  const stored = getStoredBackendUser();
  const person = stored?.person;
  if (person?.id !== personId) return null;
  return person.avatarUrl || person.avatar_url || null;
}

export function resolveAvatarUrlForPerson(personId, backendAvatarPath = null, { allowLocalFallback = true } = {}) {
  const backendPath = backendAvatarPath || getStoredAuthAvatarPath(personId) || null;
  const backendUrl = backendPath ? resolveBackendAvatarUrl(backendPath) : null;
  if (backendUrl) return backendUrl;
  if (!allowLocalFallback || isHybridDataMode()) return null;
  const localPath = personId ? getLocalAvatarPath(personId) : null;
  return localPath ? resolveBackendAvatarUrl(localPath) : null;
}

export function mergePersonAvatar(record, { idKey = "id", personIdKey = "person_id", backendAuthoritative = false } = {}) {
  const personId = record?.[personIdKey] || record?.[idKey];
  const backendPath =
    record?.avatar_url ?? record?.avatarUrl ?? getStoredAuthAvatarPath(personId) ?? null;
  if (backendAuthoritative || record?._avatarFromBackend) {
    return {
      ...record,
      avatar_url: backendPath ? resolveBackendAvatarUrl(backendPath) : null,
    };
  }
  return {
    ...record,
    avatar_url: resolveAvatarUrlForPerson(personId, backendPath, {
      allowLocalFallback: !isHybridDataMode(),
    }),
  };
}

export async function uploadOwnAvatar({ imageBase64, contentType, source = "upload", locale = "fr", labels = {} }) {
  const token = getBackendToken();
  const base = getBackendApiUrl();
  const serverNotConfirmed =
    labels.serverNotConfirmed ||
    (locale === "en"
      ? "The photo was received temporarily, but it was not confirmed by the server. It will not be kept after refresh."
      : "La photo a été reçue temporairement, mais elle n'a pas été confirmée par le serveur. Elle ne sera pas conservée après un rafraîchissement.");

  if (token && base && isHybridDataMode()) {
    const res = await fetch(`${base}/api/v1/avatars/profile`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ imageBase64, contentType, source, locale }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.messageFr || data.messageEn || "AVATAR_UPLOAD_FAILED");
      err.payload = data;
      throw err;
    }
    const diag = data.diagnostic;
    if (!data.success || !diag?.fileExists || !diag?.dbUpdated || diag?.getAvatarStatus !== 200) {
      const err = new Error(serverNotConfirmed);
      err.payload = data;
      throw err;
    }

    const me = await apiFetchMe().catch(() => null);
    const confirmedUrl = me?.user?.person?.avatarUrl || me?.user?.person?.avatar_url || null;
    if (!confirmedUrl) {
      const err = new Error(serverNotConfirmed);
      err.payload = { upload: data, me };
      throw err;
    }

    updateStoredBackendUserAvatar({ avatarUrl: confirmedUrl, person: me.user.person });
    return resolveBackendAvatarUrl(confirmedUrl);
  }
  return null;
}

export async function removeOwnAvatar({ locale = "fr" } = {}) {
  const token = getBackendToken();
  const base = getBackendApiUrl();
  const personId = getStoredBackendUser()?.personId || getStoredBackendUser()?.person?.id;
  if (token && base && isHybridDataMode()) {
    const res = await fetch(`${base}/api/v1/avatars/profile?locale=${locale}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.messageFr || data.messageEn || "AVATAR_REMOVE_FAILED");
      err.payload = data;
      throw err;
    }
    if (personId) setLocalAvatarPath(personId, null);
    updateStoredBackendUserAvatar({ avatarUrl: null, person: data.person || { id: personId, avatarUrl: null } });
    return null;
  }
  if (personId) setLocalAvatarPath(personId, null);
  return null;
}

export async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("FILE_READ_FAILED"));
    reader.readAsDataURL(file);
  });
}

export function validateAvatarFile(file, labels) {
  if (!file) return labels.invalidFile;
  const allowed = ["image/jpeg", "image/png", "image/webp"];
  if (!allowed.includes(file.type)) return labels.unsupportedFormat;
  if (file.size > 2 * 1024 * 1024) return labels.tooLarge;
  return null;
}

export function purgeEphemeralAvatarCache() {
  const store = readStore();
  let changed = false;
  for (const [key, entry] of Object.entries(store)) {
    if (entry?.path?.startsWith?.("blob:") || entry?.url?.startsWith?.("blob:")) {
      delete store[key];
      changed = true;
    }
  }
  if (changed) writeStore(store);
}
